import MaterialIcon from '@components/common/MaterialIcon';
import clsx from 'clsx';
import type React from 'react';

// * Follow-up turns hang off the first question like a tree. Each one draws its own
// * vertical segment up through the gap-2 above it, so the segments join into one line,
// * plus a tick into the middle of the card; the last one stops the line at its tick. Pseudo-elements
// * are placed from the padding box, so every offset adds the card's 1px border - that
// * keeps the line touching the neighbouring borders without running onto them.
const THREAD_CONNECTOR_CLASS =
  'relative ml-8 before:absolute before:left-[calc(-1rem_-_1px)] before:top-[calc(-0.5rem_-_1px)] before:h-[calc(100%_+_0.5rem_+_2px)] before:border-l before:border-gray-300 last:before:h-[calc(50%_+_0.5rem_+_1px)] after:absolute after:left-[calc(-1rem_-_1px)] after:top-1/2 after:-translate-y-1/2 after:w-4 after:border-t after:border-gray-300';

type ConversationExchangeItemProps = {
  question: string;
  // Every card after the first is drawn as a follow-up of the opening question.
  isFollowUp: boolean;
  isHighlighted?: boolean;
  // Shown above the question, e.g. the "reviewing" label of a highlighted card.
  header?: React.ReactNode;
  // Shown at the right end of the question row, e.g. the hot-topic pill.
  questionAside?: React.ReactNode;
  itemRef?: React.Ref<HTMLLIElement>;
  // The answer area, usually a ConversationExchangeAnswer.
  children?: React.ReactNode;
};

/**
 * One question + A.I. answer card of a conversation, shared by the chat thread
 * dialog and the Voice A.I. call dialog.
 */
export const ConversationExchangeItem: React.FC<
  ConversationExchangeItemProps
> = ({
  question,
  isFollowUp,
  isHighlighted = false,
  header,
  questionAside,
  itemRef,
  children,
}) => {
  return (
    <li
      ref={itemRef}
      className={clsx(
        'flex flex-col gap-0.5 rounded-lg border px-3 py-2.5',
        isHighlighted
          ? 'border-blue-600 bg-blue-50'
          : 'border-gray-200 bg-white',
        isFollowUp && THREAD_CONNECTOR_CLASS,
      )}>
      {header}
      <div className='flex items-start justify-between gap-3'>
        <span className='flex items-start gap-2'>
          <MaterialIcon
            icon='person'
            type='outlined'
            classes={['text-sm', 'text-gray-400', 'mt-0.5', '!leading-none']}
          />
          <span className='text-tiny text-gray-700'>{question}</span>
        </span>
        {questionAside}
      </div>
      {children}
    </li>
  );
};

type ConversationExchangeAnswerProps = {
  className?: string;
  children: React.ReactNode;
};

export const ConversationExchangeAnswer: React.FC<
  ConversationExchangeAnswerProps
> = ({ className, children }) => {
  return (
    <div className='flex items-start gap-2'>
      <MaterialIcon
        icon='smart_toy'
        type='outlined'
        classes={['text-sm', 'text-primary-base', 'mt-0.5', '!leading-none']}
      />
      <span className={clsx('text-tiny text-gray-400', className)}>
        {children}
      </span>
    </div>
  );
};
